import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, ArrowUp, ArrowDown, Wrench, RotateCcw, Settings2 } from "lucide-react";
import { toast } from "sonner";

/** Office/admin dialog to customise the org's pre-start checklist.
 *  "Mech" items auto-raise a damage report when failed. */
export function PreStartChecklistEditor({ checklist, onSaved }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setItems((checklist?.items || []).map((i) => ({ ...i })));
  }, [open, checklist]);

  const set = (i, patch) => setItems((arr) => arr.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  const remove = (i) => setItems((arr) => arr.filter((_, j) => j !== i));
  const move = (i, dir) => setItems((arr) => {
    const j = i + dir;
    if (j < 0 || j >= arr.length) return arr;
    const copy = [...arr];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  });
  const add = () => setItems((arr) => [...arr, { key: null, label: "", mech: false }]);

  const save = async (reset = false) => {
    setBusy(true);
    try {
      const { data } = await api.put("/pre_starts/checklist", { items: reset ? [] : items });
      onSaved(data);
      setOpen(false);
      toast.success(reset ? "Checklist reset to default" : "Checklist saved — applies to all new pre-starts");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}
        className="rounded-sm uppercase font-bold border-zinc-400"
        data-testid="prestart-customise-btn">
        <Settings2 className="w-3.5 h-3.5 mr-1.5" /> Customise checklist
        {checklist?.custom && <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-[#FF5F15]" />}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-xl max-h-[92vh] overflow-y-auto" data-testid="prestart-checklist-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-2xl">Pre-start checklist</DialogTitle>
            <DialogDescription>
              This is what your crew ticks off before operating. Items marked with the
              spanner auto-raise a damage report when failed. Changes apply to new pre-starts only.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            {items.map((it, i) => (
              <div key={i} className="flex items-center gap-1.5" data-testid={`checklist-item-row-${i}`}>
                <div className="flex flex-col">
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0}
                    className="text-zinc-400 hover:text-zinc-800 disabled:opacity-20 p-0.5" aria-label="Move up">
                    <ArrowUp className="w-3 h-3" />
                  </button>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1}
                    className="text-zinc-400 hover:text-zinc-800 disabled:opacity-20 p-0.5" aria-label="Move down">
                    <ArrowDown className="w-3 h-3" />
                  </button>
                </div>
                <Input value={it.label} onChange={(e) => set(i, { label: e.target.value })}
                  placeholder="Check item (e.g. Grease points)"
                  className="rounded-sm border-zinc-300 h-9 flex-1 min-w-0 text-sm"
                  data-testid={`checklist-item-label-${i}`} />
                <button type="button" onClick={() => set(i, { mech: !it.mech })}
                  title={it.mech ? "Fail auto-raises a damage report — tap to turn off" : "Tap to auto-raise a damage report on fail"}
                  className={`h-9 w-9 shrink-0 flex items-center justify-center border rounded-sm transition-colors ${it.mech ? "bg-[#FF5F15] border-[#FF5F15] text-white" : "border-zinc-300 text-zinc-400 hover:border-zinc-500"}`}
                  data-testid={`checklist-item-mech-${i}`}>
                  <Wrench className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => remove(i)}
                  className="h-9 w-8 shrink-0 flex items-center justify-center text-zinc-400 hover:text-red-600"
                  aria-label="Remove" data-testid={`checklist-item-remove-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <Button type="button" variant="outline" onClick={add}
            className="rounded-sm w-full uppercase font-bold text-xs" data-testid="checklist-add-item-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add item
          </Button>
          <DialogFooter className="gap-2 sm:justify-between">
            <Button type="button" variant="ghost" onClick={() => save(true)} disabled={busy}
              className="rounded-sm uppercase font-bold text-xs text-zinc-500 hover:text-zinc-800"
              data-testid="checklist-reset-btn">
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset to default
            </Button>
            <Button type="button" onClick={() => save(false)} disabled={busy || items.every((i) => !i.label.trim())}
              className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold"
              data-testid="checklist-save-btn">
              {busy ? "Saving…" : "Save checklist"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
